import React from 'react';
import {
  getLatestActivityTime,
  setLatestActivityTime,
  TIMEOUT_LIMIT,
  WARNING_TIMEOUT,
} from 'types/accessControl/AccessControlConstants';
import { DateTime } from 'luxon';
import { Button, Flex, Heading, IconClock, ModalDialog, Text } from '@zillow/constellation';
import { IUseTimer } from './IUseTimer';
import { ActivityWarningTracker } from './ActivityWarningTracker';

/**
 * Idea here is, when we have a last activity of less than 13 minutes we wait until the 13 minute mark to check again
 * If we check and it's more than 13 minutes we check 1/second, so we can update the shown timer.
 */
export const ActivityWarning: React.FC<IUseTimer> = ({ timer }) => {
  const timeSinceLastActivity = DateTime.now().diff(getLatestActivityTime());
  const timeRemaining = getLatestActivityTime().plus({ milliseconds: TIMEOUT_LIMIT }).diff(DateTime.now());
  const millisecondsSinceLastActivity = timeSinceLastActivity.as('milliseconds');
  const millisecondsRemaining = timeRemaining.as('milliseconds');

  return (
    <ModalDialog
      // By using timer > 5, I am hoping to have it not pop up right when you hit the page and are logged out already. Giving a touch of time
      // for other things to catch up.
      isOpen={millisecondsSinceLastActivity > WARNING_TIMEOUT && timer > 5}
      size={'xs'}
      onClose={() => setLatestActivityTime()}
      renderHeader={<Heading level={4}>Session Timeout</Heading>}
      renderBody={
        <Flex display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <ActivityWarningTracker />
          <IconClock size={'lg'} paddingBottom={'xs'} />
          {millisecondsRemaining > 0 && (
            <>
              <Text textAlign={'center'}>For security, your Pinecone session will time out in:</Text>
              <Heading level={5} textAlign={'center'}>
                {timeRemaining.toFormat('mm:ss')}
              </Heading>
            </>
          )}
          {millisecondsRemaining <= 0 && (
            <Heading level={5} textAlign={'center'}>
              Your Pinecone session has timed out.
            </Heading>
          )}
        </Flex>
      }
      renderFooter={
        <>
          {millisecondsRemaining > 0 && (
            <Button buttonType="primary" onClick={() => setLatestActivityTime()} fluid>
              I'm still here
            </Button>
          )}
        </>
      }
    />
  );
};
